/**
 * R5 review media, checked in the browser before upload (the server checks
 * again): photos are downscaled + re-encoded here so uploads stay small;
 * videos must be mp4/webm, <= 8 s, <= 4 MB, at most 720p.
 */
export const MAX_REVIEW_PHOTOS = 4;
export const VIDEO_MAX_BYTES = 4 * 1024 * 1024;
export const VIDEO_MAX_SECONDS = 8;
const VIDEO_TYPES = ['video/mp4', 'video/webm'];
const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const PHOTO_MAX_SIDE = 1600;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('unreadable')); };
    img.src = url;
  });
}

/** Resolves to a compressed File (WebP, JPEG fallback) or rejects with an i18n key. */
export async function compressPhoto(file) {
  if (!file || !PHOTO_TYPES.includes(file.type)) throw new Error('reviews.media.photoType');
  let img;
  try {
    img = await loadImage(file);
  } catch {
    throw new Error('reviews.media.photoType');
  }
  const scale = Math.min(1, PHOTO_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  const toBlob = (type) => new Promise((resolve) => canvas.toBlob(resolve, type, 0.8));
  let blob = await toBlob('image/webp');
  if (!blob || blob.type !== 'image/webp') blob = await toBlob('image/jpeg');
  if (!blob) return file;
  const ext = blob.type === 'image/webp' ? 'webp' : 'jpg';
  return new File([blob], `review-photo.${ext}`, { type: blob.type });
}

/** Resolves to the file if it passes, or rejects with an i18n key. */
export function checkVideo(file) {
  return new Promise((resolve, reject) => {
    if (!file || !VIDEO_TYPES.includes(file.type)) return reject(new Error('reviews.media.videoType'));
    if (file.size > VIDEO_MAX_BYTES) return reject(new Error('reviews.media.videoSize'));
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (!Number.isFinite(video.duration) || video.duration > VIDEO_MAX_SECONDS + 0.5) {
        return reject(new Error('reviews.media.videoLength'));
      }
      const shortSide = Math.min(video.videoWidth || 0, video.videoHeight || 0);
      if (shortSide > 720) return reject(new Error('reviews.media.videoResolution'));
      return resolve(file);
    };
    video.onerror = () => { URL.revokeObjectURL(url); reject(new Error('reviews.media.videoType')); };
    video.src = url;
  });
}
